import {
  AlertTriangle,
  Filter as FilterIcon,
  Info,
  ShieldAlert,
  ShieldCheck,
  ShieldOff,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { cycleSortDir, DataTable, type Column } from "../../components/DataTable";
import { PageShell } from "../../components/layout/PageShell";
import { Modal } from "../../components/Modal";
import { TableToolbar } from "../../components/TableToolbar";
import { Button } from "../../components/ui/button";
import {
  Sheet,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import { fetchAgentHealthFilterOptions } from "../AgentHealth/services";
import { fetchScanDashboardFilterOptions } from "../ScanCoverage/services/scanCoverageApi";
import { BlurredSheetContent } from "../itCompliance/components/BlurredSheetContent";
import { TOOLBAR_BUTTON_CLASS } from "../itCompliance/constants";
import { FilterDropdown } from "../itCompliance/components/FilterDropdown";
import { ScopeTreeFilter } from "../itCompliance/components/ScopeTreeFilter";
import { useAccessibleOrganizationsTree } from "../itCompliance/hooks";
import { fetchCmdbToolsByOrg } from "../itCompliance/services";
import {
  collectAllScopeIds,
  collectOrgScopeByType,
  scopeTriggerLabel,
  selectedScopeApplicationNames,
} from "../itCompliance/utils";
import {
  OnboardingDrilldownDrawer,
  type AssetCoverageDrilldownTarget,
} from "./OnboardingDrilldownDrawer";
import {
  ASSET_COVERAGE_TOOL_DETAIL_COLUMNS,
  ASSET_TOOL_SORT_PREFIX,
  getAssetCoverageAssetColumns,
} from "./columns";
import {
  fetchAssetCoverageAssets,
  fetchAssetCoverageAssetTools,
  fetchAssetCoverageSummary,
  type AssetCoverageAssetRow,
  type AssetCoverageSummary,
  type AssetCoverageToolDetail,
} from "./services";

// KPI cards read as a bare "0" when there's nothing to count at all, instead of "0/0".
/** Drawer options are listed A-Z. */
const sortedOptions = (opts: string[]): string[] =>
  [...opts].sort((a, b) => a.localeCompare(b));

/** Leading "#" column; `offset` is the number of rows on earlier pages. */
const rowNumberColumn = <T,>(offset: number): Column<T> => ({
  key: "rowNumber",
  header: "#",
  align: "center",
  sortable: false,
  width: "60px",
  minWidth: 50,
  render: (_row, index) => (
    <span className="text-xs font-normal text-foreground tabular-nums">{offset + index + 1}</span>
  ),
});

function formatKpiFraction(count: number, total: number): string {
  return count === 0 && total === 0 ? "0" : `${count}/${total}`;
}

export function AssetCoverageOnboarding() {
  // Draft selection only takes effect on "Filter", so switching the dropdown never re-filters
  // anything out from under someone mid-pick.
  const scopeTree = useAccessibleOrganizationsTree();
  const [draftScope, setDraftScope] = useState<string[]>([]);
  const [appliedScope, setAppliedScope] = useState<string[]>([]);

  // Seeds Application with every node once the tree resolves; guarded so it runs once and
  // never clobbers a selection the user already changed before a slow fetch settles.
  const [scopeSeeded, setScopeSeeded] = useState(false);
  useEffect(() => {
    if (scopeSeeded || scopeTree.loading) return;
    if (scopeTree.data.length > 0) {
      const allIds = collectAllScopeIds(scopeTree.data);
      setDraftScope(allIds);
      setAppliedScope(allIds);
    }
    setScopeSeeded(true);
  }, [scopeSeeded, scopeTree.loading, scopeTree.data]);

  const allScopeIds = useMemo(
    () => collectAllScopeIds(scopeTree.data),
    [scopeTree.data],
  );

  // Options come from POST /administration/api/v1/cmdb/tools/by-org, scoped to the
  // organizations the current user can access (the full accessible tree).
  const [tools, setTools] = useState<string[]>([]);
  const [draftTool, setDraftTool] = useState<string[]>([]);
  const [appliedTool, setAppliedTool] = useState<string[]>([]);
  // Guards the fetch effects below so the very first request waits for the real seeded tool
  // list instead of firing with `tools: []` while it's still loading.
  const [toolsSeeded, setToolsSeeded] = useState(false);
  // Table filters drawer: Tool selection (options = `tools` above, same API as the page's Tool dropdown).
  const [tableTools, setTableTools] = useState<string[]>([]);
  const [draftTableTools, setDraftTableTools] = useState<string[]>([]);
  // Table filters drawer: Application options come from the Scan Coverage overview filter-options API.
  const [applicationOptions, setApplicationOptions] = useState<string[]>([]);
  const [tableApps, setTableApps] = useState<string[]>([]);
  const [draftTableApps, setDraftTableApps] = useState<string[]>([]);
  useEffect(() => {
    let cancelled = false;
    fetchScanDashboardFilterOptions()
      .then((opts) => {
        if (cancelled || !opts) return;
        const names = Array.from(
          new Set((opts.applications ?? []).map((a) => a.name)),
        ).sort((a, b) => a.localeCompare(b));
        setApplicationOptions(names);
        setTableApps(names);
        setDraftTableApps(names);
      })
      .catch((err: unknown) =>
        console.error("Asset coverage application options fetch failed:", err),
      );
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    // Waits for the org tree to resolve before fetching: an empty scope means
    // "every accessible org" (same convention as AssetCoverageFilter), which is
    // exactly what the resolved tree already covers, so fetching before it
    // loads would just duplicate the same call.
    if (scopeTree.loading) return;
    let cancelled = false;
    fetchCmdbToolsByOrg(collectOrgScopeByType(scopeTree.data))
      .then((instances) => {
        if (cancelled) return;
        const list = Array.from(new Set(instances.map((t) => t.toolName))).sort(
          (a, b) => a.localeCompare(b),
        );
        if (list.length === 0) return;
        setTools(list);
        setDraftTool(list);
        setAppliedTool(list);
        setTableTools(list);
        setDraftTableTools(list);
      })
      .catch((err: unknown) => {
        console.error("Asset coverage tools fetch failed:", err);
      })
      .finally(() => {
        if (!cancelled) setToolsSeeded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [scopeTree.loading, scopeTree.data]);

  // Waits for both seeds so the page fires exactly one request per endpoint on load.
  const filtersReady = scopeSeeded && toolsSeeded;

  // The input's live value; the APIs below only react to `appliedAssetSearch`,
  // which SearchInput's onSubmit updates on Enter (or the magnifier/clear).
  const [assetSearch, setAssetSearch] = useState("");
  const [appliedAssetSearch, setAppliedAssetSearch] = useState("");

  // KPI cards: counts come from the summary endpoint over every asset matching the
  // filter, not just the current page/table.
  const appliedApplicationNames = useMemo(
    () => selectedScopeApplicationNames(scopeTree.data, appliedScope),
    [scopeTree.data, appliedScope],
  );
  // Forward exactly what's checked in the filter -- collapsing a full
  // selection down to "[]" made the API's own reading of an empty array
  // ("no filter" vs "match nothing") the one that decided what came back,
  // not the selection actually applied.
  const applicationNamesFilter = appliedApplicationNames;
  const applicationNamesKey = applicationNamesFilter.join(",");
  const appliedToolFilter = appliedTool;
  const toolKey = appliedToolFilter.join(",");

  // Bumped on every Filter/Clear click so the fetch effects below always re-run, even when the
  // applied selection ends up identical to what it already was (e.g. re-clicking Filter with no
  // changes) -- clicking the button should always call the APIs, not be deduped away.
  const [filterTrigger, setFilterTrigger] = useState(0);

  const [summary, setSummary] = useState<AssetCoverageSummary | null>(null);
  useEffect(() => {
    if (!filtersReady) return;
    let cancelled = false;
    fetchAssetCoverageSummary({
      applicationNames: applicationNamesFilter,
      tools: appliedToolFilter,
      search: appliedAssetSearch.trim() || undefined,
    })
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch((err: unknown) => {
        console.error("Asset coverage summary fetch failed:", err);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filtersReady,
    applicationNamesKey,
    toolKey,
    appliedAssetSearch,
    filterTrigger,
  ]);

  const onboardedCount = summary?.onboarding.onboarded ?? 0;
  const notOnboardedCount = summary?.onboarding.notOnboarded ?? 0;
  const totalAssets = summary?.onboarding.total ?? 0;

  const criticalAssetCount = summary?.bySeverity.critical ?? 0;
  const highAssetCount = summary?.bySeverity.high ?? 0;
  const mediumAssetCount = summary?.bySeverity.medium ?? 0;
  const lowAssetCount = summary?.bySeverity.low ?? 0;
  const totalScoredAssets = summary?.bySeverity.totalScored ?? 0;
  const [drilldown, setDrilldown] =
    useState<AssetCoverageDrilldownTarget | null>(null);
  const [assetPage, setAssetPage] = useState(0);
  const [assetPageSize, setAssetPageSize] = useState(10);
  const [assetSortKey, setAssetSortKey] = useState<string | null>(null);
  const [assetSortDir, setAssetSortDir] = useState<"asc" | "desc">("asc");
  // Table filters drawer: Environment + Type options come from the Agent Health filter-options API.
  // Everything is selected by default; the draft only applies on "Apply Filters".
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [environmentOptions, setEnvironmentOptions] = useState<string[]>([]);
  const [typeOptions, setTypeOptions] = useState<string[]>([]);
  const [tableEnvironments, setTableEnvironments] = useState<string[]>([]);
  const [tableTypes, setTableTypes] = useState<string[]>([]);
  const [draftEnvironments, setDraftEnvironments] = useState<string[]>([]);
  const [draftTypes, setDraftTypes] = useState<string[]>([]);
  const [statusOptions, setStatusOptions] = useState<string[]>([]);
  const [tableStatuses, setTableStatuses] = useState<string[]>([]);
  const [draftStatuses, setDraftStatuses] = useState<string[]>([]);
  useEffect(() => {
    let cancelled = false;
    fetchAgentHealthFilterOptions()
      .then((opts) => {
        if (cancelled || !opts) return;
        const envs = opts.environments ?? [];
        const types = opts.types ?? [];
        setEnvironmentOptions(envs);
        setTypeOptions(types);
        setTableEnvironments(envs);
        setDraftEnvironments(envs);
        setTableTypes(types);
        setDraftTypes(types);
        const statuses = opts.assetStatuses ?? [];
        setStatusOptions(statuses);
        setTableStatuses(statuses);
        setDraftStatuses(statuses);
      })
      .catch((err: unknown) =>
        console.error("Asset coverage filter options fetch failed:", err),
      );
    return () => {
      cancelled = true;
    };
  }, []);
  // Applying the page-level filter copies its Application/Tool selection into the drawer (see
  // handleApplyFilters); drawer edits never flow back. "All selected" falls back to the page-level filter.
  const tableAppsRestricted =
    applicationOptions.length > 0 &&
    tableApps.length < applicationOptions.length;
  const tableToolsRestricted =
    tools.length > 0 && tableTools.length < tools.length;
  // Send the drawer's apps whenever any are selected: with everything checked, falling back to the
  // page-level scope names dropped apps missing from the scope tree (e.g. Canara Bank).
  const tableApplicationNames =
    tableApps.length > 0 ? tableApps : applicationNamesFilter;
  const tableToolNames = tableToolsRestricted ? tableTools : appliedToolFilter;
  const tableFilterCount =
    (tableAppsRestricted ? 1 : 0) +
    (tableToolsRestricted ? 1 : 0) +
    (tableEnvironments.length < environmentOptions.length ? 1 : 0) +
    (tableTypes.length < typeOptions.length ? 1 : 0) +
    (tableStatuses.length < statusOptions.length ? 1 : 0);

  const [selectedAssetForTools, setSelectedAssetForTools] =
    useState<AssetCoverageAssetRow | null>(null);

  // Roster: filtered, sorted, and paginated server-side.
  const [assetRows, setAssetRows] = useState<AssetCoverageAssetRow[]>([]);
  const [assetTotal, setAssetTotal] = useState(0);
  useEffect(() => {
    if (!filtersReady) return;
    let cancelled = false;
    fetchAssetCoverageAssets({
      applicationNames: tableApplicationNames,
      tools: tableToolNames,
      environments:
        tableEnvironments.length > 0 ? tableEnvironments : undefined,
      types: tableTypes.length > 0 ? tableTypes : undefined,
      toolStatus: tableStatuses.length > 0 ? tableStatuses : undefined,
      search: appliedAssetSearch.trim() || undefined,
      page: assetPage + 1,
      pageSize: assetPageSize,
      ...(assetSortKey?.startsWith(ASSET_TOOL_SORT_PREFIX)
        ? { sortTool: assetSortKey.slice(ASSET_TOOL_SORT_PREFIX.length) }
        : {
            sortKey:
              (assetSortKey as
                | "name"
                | "type"
                | "ip"
                | "environment"
                | "application"
                | "applications") ?? undefined,
          }),
      sortDir: assetSortDir,
    })
      .then((data) => {
        if (cancelled) return;
        setAssetRows(data.items);
        setAssetTotal(data.total);
      })
      .catch((err: unknown) => {
        console.error("Asset coverage assets fetch failed:", err);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filtersReady,
    applicationNamesKey,
    toolKey,
    tableApps,
    tableTools,
    appliedAssetSearch,
    tableEnvironments,
    tableTypes,
    tableStatuses,
    assetPage,
    assetPageSize,
    assetSortKey,
    assetSortDir,
    filterTrigger,
  ]);

  const [selectedTool, setSelectedTool] = useState<string | null>(null);
  const [toolDetailRows, setToolDetailRows] = useState<
    AssetCoverageToolDetail[]
  >([]);
  const [toolDetailLoading, setToolDetailLoading] = useState(false);
  useEffect(() => {
    if (!selectedAssetForTools || !selectedTool) {
      setToolDetailRows([]);
      return;
    }
    let cancelled = false;
    setToolDetailLoading(true);
    fetchAssetCoverageAssetTools(selectedAssetForTools.id, selectedTool)
      .then((rows) => {
        if (!cancelled) setToolDetailRows(rows);
      })
      .catch((err: unknown) => {
        console.error("Asset coverage tool detail fetch failed:", err);
        if (!cancelled) setToolDetailRows([]);
      })
      .finally(() => {
        if (!cancelled) setToolDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedAssetForTools, selectedTool]);

  const handleAssetSort = (key: string) => {
    const next = cycleSortDir(assetSortKey, assetSortDir, key);
    setAssetSortKey(next.sortKey);
    setAssetSortDir(next.sortDir);
    setAssetPage(0);
  };

  const assetColumns = [
    rowNumberColumn<AssetCoverageAssetRow>(assetPage * assetPageSize),
    ...getAssetCoverageAssetColumns(assetRows, (asset, tool) => {
      setSelectedTool(tool);
      setSelectedAssetForTools(asset);
    }),
  ];

  const filtersActive =
    (appliedScope.length > 0 && appliedScope.length < allScopeIds.length) ||
    appliedTool.length > 0;

  // Filter is a no-op until the draft differs from what is already applied.
  const sameSelection = (a: string[], b: string[]) =>
    a.length === b.length && a.every((v) => b.includes(v));
  const filtersUnchanged =
    sameSelection(draftScope, appliedScope) &&
    sameSelection(draftTool, appliedTool);

  const filtersCleared =
    draftScope.length === 0 &&
    draftTool.length === 0 &&
    appliedScope.length === 0 &&
    appliedTool.length === 0;

  // Mirrors the page-level Application/Tool selection into the table filters drawer (empty = all).
  const syncTableFilters = (scope: string[], toolSel: string[]) => {
    const apps = selectedScopeApplicationNames(scopeTree.data, scope).filter(
      (a) => applicationOptions.includes(a),
    );
    const nextApps = apps.length > 0 ? apps : applicationOptions;
    const nextTools = toolSel.length > 0 ? toolSel : tools;
    setTableApps(nextApps);
    setDraftTableApps(nextApps);
    setTableTools(nextTools);
    setDraftTableTools(nextTools);
  };

  const handleApplyFilters = () => {
    setAppliedScope(draftScope);
    setAppliedTool(draftTool);
    syncTableFilters(draftScope, draftTool);
    setAssetPage(0);
    setFilterTrigger((v) => v + 1);
  };

  // Clear empties the selections and applies them; disabled once that is already the case.
  const handleClearFilters = () => {
    setDraftScope([]);
    setAppliedScope([]);
    setDraftTool([]);
    setAppliedTool([]);
    syncTableFilters([], []);
    setAssetPage(0);
    setFilterTrigger((v) => v + 1);
  };

  return (
    <PageShell className="space-y-5">
      <header className="border-b border-border pb-4">
        <h1 className="text-xl font-bold leading-tight tracking-tight text-foreground">
          Asset Coverage
        </h1>
        <p className="mt-1 max-w-2xl text-xs leading-relaxed text-muted-foreground">
          Monitor security tool onboarding and coverage status across assets.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <ScopeTreeFilter
          data={scopeTree.data}
          value={draftScope}
          onChange={setDraftScope}
          loading={scopeTree.loading}
          variant="pill"
          triggerLabel={scopeTriggerLabel(
            scopeTree.data,
            draftScope,
            "Application",
          )}
        />
        <FilterDropdown
          label="Tool"
          options={tools}
          value={draftTool}
          onChange={setDraftTool}
          searchPlaceholder="Search"
          variant="pill"
        />
        <Button
          type="button"
          size="sm"
          onClick={handleApplyFilters}
          disabled={filtersUnchanged}
          aria-label="Apply filters"
        >
          Filter
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleClearFilters}
          disabled={filtersCleared}
          aria-label="Clear filters"
        >
          Clear
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[35%_65%]">
        <div className="rounded-xl border border-[#eef2f6] dark:border-white/[0.05] bg-white dark:bg-white/[0.02] p-4">
          <h2 className="text-sm font-semibold text-foreground">
            Onboarding Status
          </h2>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[repeat(2,minmax(0,1fr))] [@media(min-width:1015px)_and_(max-width:1800px)]:grid-cols-1">
            <Button
              variant="none"
              size="none"
              type="button"
              onClick={() =>
                setDrilldown({ kind: "onboarding", status: "onboarded" })
              }
              aria-label="View onboarded assets"
              className="group flex w-full cursor-pointer items-center justify-start gap-3 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 px-4 py-3 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:shadow-emerald-500/10"
            >
              <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-emerald-500 text-white transition-transform duration-200 group-hover:scale-110">
                <ShieldCheck aria-hidden className="size-4" />
              </span>
              <span className="text-sm font-medium text-foreground">
                Onboarded
              </span>
              <span className="ml-auto text-sm font-semibold text-foreground">
                {formatKpiFraction(onboardedCount, totalAssets)}
              </span>
            </Button>
            <Button
              variant="none"
              size="none"
              type="button"
              onClick={() =>
                setDrilldown({ kind: "onboarding", status: "not-onboarded" })
              }
              aria-label="View not onboarded assets"
              className="group flex w-full cursor-pointer items-center justify-start gap-3 rounded-lg bg-red-50 dark:bg-red-500/10 px-4 py-3 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:shadow-red-500/10"
            >
              <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-red-500 text-white transition-transform duration-200 group-hover:scale-110">
                <ShieldOff aria-hidden className="size-4" />
              </span>
              <span className="text-sm font-medium text-foreground">
                Not Onboarded
              </span>
              <span className="ml-auto text-sm font-semibold text-foreground">
                {formatKpiFraction(notOnboardedCount, totalAssets)}
              </span>
            </Button>
          </div>
        </div>

        <div className="rounded-xl border border-[#eef2f6] dark:border-white/[0.05] bg-white dark:bg-white/[0.02] p-4">
          <h2 className="text-sm font-semibold text-foreground">
            Assets By Asset Score
          </h2>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4 [@media(min-width:1015px)_and_(max-width:1800px)]:grid-cols-2">
            <Button
              variant="none"
              size="none"
              type="button"
              onClick={() =>
                setDrilldown({ kind: "severity", severity: "Critical" })
              }
              aria-label="View critical assets"
              className="group flex w-full cursor-pointer items-center justify-start gap-3 rounded-lg bg-red-50 dark:bg-red-500/10 px-3 py-3 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:shadow-red-500/10"
            >
              <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-red-500 text-white transition-transform duration-200 group-hover:scale-110">
                <ShieldAlert aria-hidden className="size-4" />
              </span>
              <span className="text-sm font-medium text-foreground">
                Critical
              </span>
              <span className="ml-auto text-sm font-semibold text-foreground">
                {formatKpiFraction(criticalAssetCount, totalScoredAssets)}
              </span>
            </Button>
            <Button
              variant="none"
              size="none"
              type="button"
              onClick={() =>
                setDrilldown({ kind: "severity", severity: "High" })
              }
              aria-label="View high assets"
              className="group flex w-full cursor-pointer items-center justify-start gap-3 rounded-lg bg-orange-50 dark:bg-orange-500/10 px-3 py-3 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:shadow-orange-500/10"
            >
              <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-orange-500 text-white transition-transform duration-200 group-hover:scale-110">
                <AlertTriangle aria-hidden className="size-4" />
              </span>
              <span className="text-sm font-medium text-foreground">High</span>
              <span className="ml-auto text-sm font-semibold text-foreground">
                {formatKpiFraction(highAssetCount, totalScoredAssets)}
              </span>
            </Button>
            <Button
              variant="none"
              size="none"
              type="button"
              onClick={() =>
                setDrilldown({ kind: "severity", severity: "Medium" })
              }
              aria-label="View medium assets"
              className="group flex w-full cursor-pointer items-center justify-start gap-3 rounded-lg bg-amber-50 dark:bg-amber-500/10 px-3 py-3 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:shadow-amber-500/10"
            >
              <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-amber-500 text-white transition-transform duration-200 group-hover:scale-110">
                <Info aria-hidden className="size-4" />
              </span>
              <span className="text-sm font-medium text-foreground">
                Medium
              </span>
              <span className="ml-auto text-sm font-semibold text-foreground">
                {formatKpiFraction(mediumAssetCount, totalScoredAssets)}
              </span>
            </Button>
            <Button
              variant="none"
              size="none"
              type="button"
              onClick={() =>
                setDrilldown({ kind: "severity", severity: "Low" })
              }
              aria-label="View low assets"
              className="group flex w-full cursor-pointer items-center justify-start gap-3 rounded-lg bg-sky-50 dark:bg-sky-500/10 px-3 py-3 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:shadow-sky-500/10"
            >
              <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-sky-500 text-white transition-transform duration-200 group-hover:scale-110">
                <Info aria-hidden className="size-4" />
              </span>
              <span className="text-sm font-medium text-foreground">Low</span>
              <span className="ml-auto text-sm font-semibold text-foreground">
                {formatKpiFraction(lowAssetCount, totalScoredAssets)}
              </span>
            </Button>
          </div>
        </div>
      </div>

      <div>
        <TableToolbar
          search={{
            value: assetSearch,
            onChange: setAssetSearch,
            onSubmit: (value) => {
              setAppliedAssetSearch(value);
              setAssetPage(0);
            },
            placeholder: "Search",
            ariaLabel: "Search assets",
          }}
          pageSize={assetPageSize}
          onPageSizeChange={(n) => {
            setAssetPageSize(n);
            setAssetPage(0);
          }}
          pageSizeLabel="Rows per page"
          trailing={
            <button
              type="button"
              onClick={() => {
                setDraftEnvironments(tableEnvironments);
                setDraftTypes(tableTypes);
                setFiltersOpen(true);
              }}
              className={TOOLBAR_BUTTON_CLASS}
            >
              <FilterIcon className="h-4 w-4" />
              Filters
              {tableFilterCount > 0 && (
                <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary/10 px-1.5 text-xs font-semibold text-primary">
                  {tableFilterCount}
                </span>
              )}
            </button>
          }
        />

        <div className="mt-3">
          <DataTable
            maxHeight="500px"
            columns={assetColumns}
            data={assetRows}
            emptyText="No assets found"
            page={assetPage}
            pageSize={assetPageSize}
            totalItems={assetTotal}
            onPageChange={setAssetPage}
            itemLabel="assets"
            sortKey={assetSortKey}
            sortDir={assetSortDir}
            onSort={handleAssetSort}
          />
        </div>
      </div>

      <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
        <BlurredSheetContent
          side="right"
          overlayClassName="bg-transparent backdrop-blur-none"
          className="flex h-full max-h-screen w-[min(50vw,42rem)] flex-col gap-0 border-l border-border bg-card p-0 max-sm:w-full"
        >
          <SheetHeader className="gap-1 border-b border-border px-3 py-2">
            <SheetTitle className="text-base font-semibold">
              Select Filters
            </SheetTitle>
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto p-4 flex flex-col gap-5">
            <div className="flex items-center justify-end">
              <button
                type="button"
                onClick={() => {
                  setDraftTableApps(applicationOptions);
                  setDraftTableTools(tools);
                  setDraftEnvironments(environmentOptions);
                  setDraftTypes(typeOptions);
                  setDraftStatuses(statusOptions);
                }}
                className="text-sm font-semibold text-blue-600 underline-offset-2 hover:underline dark:text-blue-400"
              >
                Reset Filters
              </button>
            </div>
            <FilterDropdown
              label="Application"
              options={sortedOptions(applicationOptions)}
              value={draftTableApps}
              onChange={setDraftTableApps}
              searchPlaceholder="Search"
              disabled={applicationOptions.length === 0}
              size="sm"
              surface="input"
              variant="pill"
              className="w-full px-4"
              modal
            />
            <FilterDropdown
              label="Environment"
              options={sortedOptions(environmentOptions)}
              value={draftEnvironments}
              onChange={setDraftEnvironments}
              searchPlaceholder="Search"
              disabled={environmentOptions.length === 0}
              size="sm"
              surface="input"
              variant="pill"
              className="w-full px-4"
              modal
            />
            <FilterDropdown
              label="Onboarding Status"
              options={sortedOptions(statusOptions)}
              value={draftStatuses}
              onChange={setDraftStatuses}
              searchPlaceholder="Search"
              disabled={statusOptions.length === 0}
              size="sm"
              surface="input"
              variant="pill"
              className="w-full px-4"
              modal
            />
            <FilterDropdown
              label="Tool"
              options={sortedOptions(tools)}
              value={draftTableTools}
              onChange={setDraftTableTools}
              searchPlaceholder="Search"
              disabled={tools.length === 0}
              size="sm"
              surface="input"
              variant="pill"
              className="w-full px-4"
              modal
            />
            <FilterDropdown
              label="Type"
              options={sortedOptions(typeOptions)}
              value={draftTypes}
              onChange={setDraftTypes}
              searchPlaceholder="Search"
              disabled={typeOptions.length === 0}
              size="sm"
              surface="input"
              variant="pill"
              className="w-full px-4"
              modal
            />
          </div>
          <SheetFooter className="border-t border-border flex-row justify-between">
            <button
              type="button"
              onClick={() => {
                setDraftTableApps(applicationOptions);
                setDraftTableTools(tools);
                setTableApps(applicationOptions);
                setTableTools(tools);
                setDraftEnvironments(environmentOptions);
                setDraftTypes(typeOptions);
                setTableEnvironments(environmentOptions);
                setTableTypes(typeOptions);
                setDraftStatuses(statusOptions);
                setTableStatuses(statusOptions);
                setAssetPage(0);
                setFiltersOpen(false);
              }}
              className="px-3 py-1.5 text-sm font-medium rounded-md border border-border hover:bg-accent/60"
            >
              Clear and Close
            </button>
            <button
              type="button"
              onClick={() => {
                setTableApps(draftTableApps);
                setTableTools(draftTableTools);
                setTableEnvironments(draftEnvironments);
                setTableTypes(draftTypes);
                setTableStatuses(draftStatuses);
                setAssetPage(0);
                setFiltersOpen(false);
              }}
              className="px-3 py-1.5 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
            >
              Apply Filters
            </button>
          </SheetFooter>
        </BlurredSheetContent>
      </Sheet>

      <OnboardingDrilldownDrawer
        target={drilldown}
        onClose={() => setDrilldown(null)}
        applicationNames={applicationNamesFilter}
        tools={appliedToolFilter}
        filterOptions={{
          applications: applicationOptions,
          tools,
          environments: environmentOptions,
          types: typeOptions,
          statuses: statusOptions,
        }}
        refreshKey={filterTrigger}
      />

      <Modal
        open={selectedAssetForTools !== null}
        onClose={() => setSelectedAssetForTools(null)}
        title={
          selectedAssetForTools?.name ?? selectedAssetForTools?.assetName ?? ""
        }
        wide
      >
        {selectedAssetForTools && (
          <DataTable
            columns={[
              rowNumberColumn<AssetCoverageToolDetail>(0),
              ...ASSET_COVERAGE_TOOL_DETAIL_COLUMNS,
            ]}
            data={toolDetailRows}
            emptyText={toolDetailLoading ? "Loading..." : "No tools tracked"}
            compact
            frameless
          />
        )}
      </Modal>
    </PageShell>
  );
}
